import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { AUTH_TYPE, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { awsconfig } from 'src/config';
import { trackGraphQLError } from './sentryUtils';
import { migrationFlags } from './migrationFlags';
import { getAuthClient, initializeAuthClient } from 'src/lib/aws/cognitoAuthClient';

// Initialize auth client with configuration from awsconfig
if (typeof window !== 'undefined' && migrationFlags.useAwsSdkGraphQL) {
  initializeAuthClient({
    userPoolId: awsconfig.aws_user_pools_id,
    clientId: awsconfig.aws_user_pools_web_client_id,
    region: awsconfig.aws_cognito_region,
  });
}

const config = {
  url: awsconfig.aws_appsync_graphqlEndpoint,
  region: awsconfig.aws_appsync_region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => {
      try {
        // AppSync requires ID token for Cognito User Pools authentication
        const authClient = getAuthClient();
        const token = await authClient.getValidIdToken();
        return token;
      } catch (error) {
        // If no token is available, try to read directly from localStorage as fallback
        if (typeof window !== 'undefined') {
          const idToken = window.localStorage?.getItem('idToken');
          if (idToken) {
            return idToken;
          }
        }
        // Return empty string if no token is available (user not logged in)
        console.warn('No authentication token available');
        return '';
      }
    },
  },
};
const httpLink = createHttpLink({ uri: awsconfig.aws_appsync_graphqlEndpoint });

// Error link for tracking GraphQL errors
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      const error = new Error(`GraphQL error: ${message}`);
      trackGraphQLError(error, operation.operationName || 'unknown', operation.variables);
    });
  }

  if (networkError) {
    trackGraphQLError(networkError, operation.operationName || 'unknown', operation.variables);
  }
});

const authLink = setContext(async (_, { headers }) => {
  if (!migrationFlags.useAwsSdkGraphQL) {
    return { headers };
  }

  // On the server, skip interactive token retrieval and rely on provided headers.
  if (typeof window === 'undefined') {
    return { headers };
  }

  try {
    const authClient = getAuthClient();
    // AppSync requires ID token for Cognito User Pools authentication
    const token = await authClient.getValidIdToken();

    return {
      headers: {
        ...headers,
        // For AppSync + Cognito User Pools, Authorization should be the raw JWT
        authorization: token || '',
      },
    };
  } catch (error) {
    // Fallback to directly reading from localStorage
    if (typeof window !== 'undefined') {
      const idToken = window.localStorage?.getItem('idToken');
      if (idToken) {
        return {
          headers: {
            ...headers,
            authorization: idToken,
          },
        };
      }
    }

    // If no token available, return headers without authorization
    // This might happen when user is not logged in
    console.warn('Failed to get auth token for GraphQL:', error);
    return { headers };
  }
});

function createApolloLinks() {
  if (migrationFlags.useAwsSdkGraphQL) {
    return ApolloLink.from([errorLink, authLink, httpLink]);
  }

  return ApolloLink.from([
    errorLink,
    // @ts-ignore
    createAuthLink(config),
    // @ts-ignore
    createSubscriptionHandshakeLink(config, httpLink),
  ]);
}

export const client = new ApolloClient({
  link: createApolloLinks(),
  cache: new InMemoryCache(),
});
